/**
 * FLOOR.JSX - Composant Floor avec Dégradé et Glow
 * 
 * Composant React Three Fiber qui affiche un sol avec :
 * - Dégradé vertical (2 couleurs configurables)
 * - Effet de glow paramétrable (position, rayon, couleur, alpha)
 * - Shaders GLSL personnalisés
 * - Interface de debug avec Leva
 * 
 * TECHNIQUE :
 * - Génération d'une DataTexture 1x2 pixels pour le dégradé (optimisé)
 * - Three.js interpole automatiquement entre les 2 pixels (LinearFilter)
 * - Le shader fragment applique le dégradé + l'effet de glow
 * - Le vertex shader positionne le plan en fullscreen (z=1.0)
 * 
 * UNIFORMES :
 * - tBackground : Texture du dégradé (DataTexture 1x2)
 * - uGlowPosition : Position du centre du glow (vec2, 0-1)
 * - uGlowRadius : Rayon d'influence du glow (float, 0-2)
 * - uGlowColor : Couleur du glow (vec3, RGB)
 * - uGlowAlpha : Intensité du glow (float, 0-1)
 */

import { useMemo, useEffect, useRef } from 'react'
import { useControls, button } from 'leva'
import * as THREE from 'three'
import vertexShader from '../shaders/floor/vertex.glsl'
import fragmentShader from '../shaders/floor/fragment.glsl'

/**
 * Props :
 * - background (optionnel) : Objet avec { gradient: { top, bottom }, glow: { color, positionX, positionY, alpha } }
 *   Si fourni, override les valeurs par défaut
 */
function Floor({ background = null }) {
  const materialRef = useRef()
  const previousBackgroundIdRef = useRef(null)

  // ============================================================
  // CONTRÔLES LEVA (Interface de Debug)
  // ============================================================

  // Valeurs par défaut du Floor
  // const defaults = {
  //   gradient: {
  //     top: '#2c2248',
  //     bottom: '#4d4467'
  //   },
  //   glow: {
  //     enabled: true,
  //     positionX: 0.5,
  //     positionY: 0.5,
  //     radius: 0.5,
  //     color: '#ffcfe0',
  //     alpha: 0.3
  //   }
  // }
  const defaults = {
    gradient: {
      top: '#8064cc',
      bottom: '#5423e6'
    },
    glow: {
      enabled: true,
      positionX: 0.58,
      positionY: 0.42,
      radius: 0.71,
      color: '#ffa1c2',
      alpha: 0.68
    }
  }

  // Contrôles pour les couleurs du dégradé
  // Initialisés avec les defaults, mis à jour via setGradient quand le background change
  const [gradient, setGradient] = useControls(
    'Floor',
    () => ({
      top: {
        value: defaults.gradient.top,
        label: 'Couleur Haut'
      },
      bottom: {
        value: defaults.gradient.bottom,
        label: 'Couleur Bas'
      }
    }),
    { collapsed: true }
  )

  // Contrôles pour l'effet de glow
  // Initialisés avec les defaults, mis à jour via setGlow quand le background change
  const [glow, setGlow] = useControls(
    'Floor.glow',
    () => ({
      enabled: {
        value: defaults.glow.enabled,
        label: 'Activé'
      },
      positionX: {
        value: defaults.glow.positionX,
        min: 0,
        max: 1,
        step: 0.001,
        label: 'Position X'
      },
      positionY: {
        value: defaults.glow.positionY,
        min: 0,
        max: 1,
        step: 0.001,
        label: 'Position Y'
      },
      radius: {
        value: defaults.glow.radius,
        min: 0,
        max: 2,
        step: 0.001,
        label: 'Rayon'
      },
      color: {
        value: defaults.glow.color,
        label: 'Couleur'
      },
      alpha: {
        value: defaults.glow.alpha,
        min: 0,
        max: 1,
        step: 0.001,
        label: 'Intensité'
      }
    }),
    { collapsed: true }
  )

  // ============================================================
  // MISE À JOUR LORS DU CHANGEMENT DE BACKGROUND
  // ============================================================
  
  /**
   * Met à jour les contrôles Leva UNIQUEMENT quand l'ID du background change.
   * Cela permet :
   * - De changer les valeurs quand on sélectionne un nouveau background
   * - De continuer à modifier manuellement les valeurs dans Leva sans qu'elles soient écrasées
   * 
   * Les backgrounds sont des templates, pas des valeurs figées.
   */
  useEffect(() => {
    const currentBackgroundId = background?.id || null
    
    // Ne mettre à jour que si l'ID du background a changé
    if (currentBackgroundId !== previousBackgroundIdRef.current) {
      previousBackgroundIdRef.current = currentBackgroundId
      
      if (background) {
        // Merge du background avec les defaults pour les propriétés manquantes
        setGradient({
          top: background.gradient?.top || defaults.gradient.top,
          bottom: background.gradient?.bottom || defaults.gradient.bottom
        })
        setGlow({
          enabled: background.glow?.enabled !== undefined ? background.glow.enabled : defaults.glow.enabled,
          positionX: background.glow?.positionX !== undefined ? background.glow.positionX : defaults.glow.positionX,
          positionY: background.glow?.positionY !== undefined ? background.glow.positionY : defaults.glow.positionY,
          radius: background.glow?.radius !== undefined ? background.glow.radius : defaults.glow.radius,
          color: background.glow?.color || defaults.glow.color,
          alpha: background.glow?.alpha !== undefined ? background.glow.alpha : defaults.glow.alpha
        })
      }
    }
  }, [background, setGradient, setGlow])

  // Boutons d'action (séparés pour pouvoir accéder à gradient et glow)
  useControls(
    'Floor.actions',
    {
      logValues: button(() => {
        console.log('🎨 Floor - Valeurs actuelles :')
        console.log('\n🌈 Gradient :')
        console.log(`  top: "${gradient.top}"`)
        console.log(`  bottom: "${gradient.bottom}"`)
        console.log('\n✨ Glow :')
        console.log(`  enabled: ${glow.enabled}`)
        console.log(`  positionX: ${glow.positionX}`)
        console.log(`  positionY: ${glow.positionY}`)
        console.log(`  radius: ${glow.radius}`)
        console.log(`  color: "${glow.color}"`)
        console.log(`  alpha: ${glow.alpha}`)
        console.log('\n📋 Code à copier dans defaults :')
        console.log(`gradient: {`)
        console.log(`  top: "${gradient.top}",`)
        console.log(`  bottom: "${gradient.bottom}"`)
        console.log(`},`)
        console.log(`glow: {`)
        console.log(`  enabled: ${glow.enabled},`)
        console.log(`  positionX: ${glow.positionX},`)
        console.log(`  positionY: ${glow.positionY},`)
        console.log(`  radius: ${glow.radius},`)
        console.log(`  color: "${glow.color}",`)
        console.log(`  alpha: ${glow.alpha}`)
        console.log(`}`)
      }, { label: '📋 Log Values' }),
      reset: button(() => {
        // Reset vers le background actuel (si défini) ou vers les defaults
        const resetTo = background || defaults
        setGradient({
          top: resetTo.gradient.top,
          bottom: resetTo.gradient.bottom
        })
        setGlow({
          enabled: resetTo.glow.enabled,
          positionX: resetTo.glow.positionX,
          positionY: resetTo.glow.positionY,
          radius: resetTo.glow.radius,
          color: resetTo.glow.color,
          alpha: resetTo.glow.alpha
        })
      }, { label: '🔄 Reset' })
    },
    { collapsed: true },
    [gradient, glow, setGradient, setGlow, background]
  )

  // ============================================================
  // GÉNÉRATION DE LA DATATEXTURE (Dégradé)
  // ============================================================

  /**
   * Génère une DataTexture 1x2 pixels pour le dégradé vertical.
   * 
   * EXPLICATION :
   * - On crée une texture de 2 pixels (1 en largeur, 2 en hauteur)
   * - Pixel 0 (bas) : couleur bottom
   * - Pixel 1 (haut) : couleur top
   * - Three.js interpole automatiquement entre ces 2 pixels grâce au LinearFilter
   * - Les couleurs sont converties en sRGB pour un rendu correct
   * 
   * Cette technique est plus performante qu'un dégradé CSS/SVG car elle
   * utilise directement la GPU pour l'interpolation.
   */
  const backgroundTexture = useMemo(() => {
    // Création des objets Color Three.js
    const topColor = new THREE.Color(gradient.top)
    const bottomColor = new THREE.Color(gradient.bottom)

    // Conversion en espace colorimétrique sRGB
    topColor.convertLinearToSRGB()
    bottomColor.convertLinearToSRGB()

    // Création du tableau de données : [bas, haut]
    // Chaque pixel = 4 valeurs (RGBA)
    const data = new Uint8Array([
      Math.round(bottomColor.r * 255),
      Math.round(bottomColor.g * 255),
      Math.round(bottomColor.b * 255),
      255, // Alpha
      Math.round(topColor.r * 255),
      Math.round(topColor.g * 255),
      Math.round(topColor.b * 255),
      255  // Alpha
    ])

    // Création de la DataTexture 1x2
    const texture = new THREE.DataTexture(data, 1, 2)
    texture.magFilter = THREE.LinearFilter // Interpolation linéaire
    texture.needsUpdate = true

    return texture
  }, [gradient.top, gradient.bottom])

  // ============================================================
  // UNIFORMES DU SHADER
  // ============================================================

  /**
   * Initialisation des uniformes une seule fois.
   * 
   * Les objets sont créés une seule fois pour éviter les re-créations inutiles.
   * La mise à jour des valeurs se fait via useEffect.
   */
  const uniforms = useMemo(() => ({
    tBackground: { value: null },
    uGlowPosition: { value: new THREE.Vector2() },
    uGlowRadius: { value: 0 },
    uGlowColor: { value: new THREE.Color() },
    uGlowAlpha: { value: 0 }
  }), [])

  // ============================================================
  // MISE À JOUR DES UNIFORMES
  // ============================================================

  /**
   * Met à jour les valeurs des uniformes quand les contrôles Leva changent.
   * 
   * Cette approche permet une mise à jour en temps réel sans recréer
   * les objets Three.js, ce qui est plus performant.
   */
  useEffect(() => {
    // Mise à jour de la texture de fond (dégradé)
    uniforms.tBackground.value = backgroundTexture
  }, [backgroundTexture, uniforms])

  useEffect(() => {
    // Mise à jour de la position du glow
    uniforms.uGlowPosition.value.set(glow.positionX, glow.positionY)
    
    // Mise à jour du rayon du glow
    uniforms.uGlowRadius.value = glow.radius
    
    // Mise à jour de la couleur du glow
    uniforms.uGlowColor.value.set(glow.color)
    
    // Mise à jour de l'intensité du glow (0 si désactivé)
    uniforms.uGlowAlpha.value = glow.enabled ? glow.alpha : 0
  }, [glow, uniforms])

  // ============================================================
  // GÉOMÉTRIE
  // ============================================================

  /**
   * PlaneGeometry 2x2 avec 1 subdivision.
   * 
   * Le vertex shader positionne ce plan en z=1.0 pour qu'il couvre
   * tout l'écran, quel que soit l'angle de la caméra.
   */
  const geometry = useMemo(() => new THREE.PlaneGeometry(2, 2, 1, 1), [])

  // ============================================================
  // RENDU
  // ============================================================

  return (
    <mesh 
      geometry={geometry} 
      frustumCulled={false}
      receiveShadow  // Le mesh reçoit les ombres projetées par les objets
    >
      <shaderMaterial
        ref={materialRef}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        transparent={false}
        wireframe={false}
      />
    </mesh>
  )
}

export default Floor

